import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Info, Lock, Clock, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMedicalRecordLimits } from '@/hooks/useMedicalRecordLimits';
import { useTranslation } from 'react-i18next';

interface MedicalRecordLimitBannerProps {
  petId: string;
  type?: 'warning' | 'info' | 'limit';
}

export const MedicalRecordLimitBanner = ({ petId, type = 'info' }: MedicalRecordLimitBannerProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    currentCount, 
    maxRecords, 
    remainingRecords, 
    hasReachedLimit, 
    isUnlimited,
    currentTier,
    isInTrial,
    trialDaysRemaining
  } = useMedicalRecordLimits(petId);

  // Show trial banner for free tier users
  if (isInTrial && trialDaysRemaining !== undefined) {
    const isTrialEndingSoon = trialDaysRemaining <= 5;
    
    return (
      <Alert className={`mb-4 ${isTrialEndingSoon ? 'bg-amber-50 border-amber-300' : 'bg-blue-50 border-blue-300'}`}>
        {isTrialEndingSoon ? <Clock className="h-4 w-4 text-amber-600" /> : <Sparkles className="h-4 w-4 text-blue-600" />}
        <AlertTitle className={isTrialEndingSoon ? 'text-amber-900' : 'text-blue-900'}>
          {isTrialEndingSoon ? `⚠️ ${t('trial.bannerEndingSoon')}` : `✨ ${t('trial.bannerActive')}`}
        </AlertTitle>
        <AlertDescription className={`mt-2 ${isTrialEndingSoon ? 'text-amber-800' : 'text-blue-800'}`}>
          <p className="mb-2">
            {t('trial.youHave')} <strong>{t('trial.daysRemaining', { count: trialDaysRemaining })}</strong> {t('trial.remainingInTrial')}
            {isTrialEndingSoon && ` ${t('trial.afterTrialEnds')}`}
          </p>
          {isTrialEndingSoon && (
            <Button
              size="sm"
              onClick={() => navigate('/subscription')}
              className="mt-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white"
            >
              {t('trial.upgradeNow')}
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  // Show read-only banner for free tier users after trial
  if (currentTier === 'free' && !isInTrial && maxRecords === 0) {
    return (
      <Alert variant="destructive" className="mb-4">
        <Lock className="h-4 w-4" />
        <AlertTitle>{t('trial.readOnlyAccess')}</AlertTitle>
        <AlertDescription className="mt-2">
          <p className="mb-2">
            {t('trial.trialEnded')}
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/subscription')}
            className="mt-2"
          >
            {t('trial.upgradeSubscription')}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (isUnlimited) return null;

  const showWarning = type === 'warning' && remainingRecords <= 5 && remainingRecords > 0;
  const showLimit = type === 'limit' && hasReachedLimit;
  const showInfo = type === 'info' && !hasReachedLimit;

  if (!showWarning && !showLimit && !showInfo) return null;

  const getIcon = () => {
    if (showLimit) return <Lock className="h-4 w-4" />;
    if (showWarning) return <AlertTriangle className="h-4 w-4" />;
    return <Info className="h-4 w-4" />;
  };

  const getVariant = (): 'default' | 'destructive' => {
    if (showLimit || showWarning) return 'destructive';
    return 'default';
  };

  const getTitle = () => {
    if (showLimit) return 'Medical Record Limit Reached';
    if (showWarning) return 'Approaching Medical Record Limit';
    return 'Medical Records';
  };

  const getDescription = () => {
    if (showLimit) {
      return `You've reached your ${currentTier} plan limit of ${maxRecords} total medical records across all your pets. Upgrade to add more records.`;
    }
    if (showWarning) {
      return `You have ${remainingRecords} of ${maxRecords} total medical records remaining across all your pets. Consider upgrading soon.`;
    }
    return `You're using ${currentCount} of ${maxRecords} total medical records across all your pets.`;
  };

  return (
    <Alert variant={getVariant()} className="mb-4">
      {getIcon()}
      <AlertTitle>{getTitle()}</AlertTitle>
      <AlertDescription className="mt-2">
        <p className="mb-2">{getDescription()}</p>
        {(showLimit || showWarning) && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/subscription')}
            className="mt-2"
          >
            Upgrade Subscription
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
};
